import { match } from 'ts-pattern';

import type { ProductDeviceInCart, ProductVariant } from '@telialt/models/src/EshopModel';

import { DEFAULT_CURRENCY, DEFAULT_QUANTITY, normalizeCommitmentType, normalizeSegment } from '../../core/normalizers';
import type { GTMUserData } from '../../core/user';
import { GTMUserType } from '../../core/user';
import type { TrackingCommerce, TrackingItem } from '../../types';
import { GTMItemServiceType, GTMItemType, GTMOrderType } from '../../types';
import type { HudsonCheckoutCart, HudsonCheckoutCartEntry, HudsonCheckoutEventData } from './types';

const HUDSON_PAYMENT_METHOD = {
    BankPayment: 'BLK',
    CourierPayment: 'COD',
} as const;

const CHECKOUT_PAYMENT_TYPE = {
    FullPrice: 'Full Price',
    Installment: 'Installment',
} as const;

const FIXED_SERVICE_EQUIPMENT_OR_VAS_ITEM_TYPES = [
    'EquipmentRouter',
    'EquipmentTVBox',
    'VasSmartWifi',
    'VAS',
    'VAS_HT',
];
const FIXED_SERVICE_VAS_CODES = ['HBOMAX_STANDARD', 'HBO', 'NETFLIX'];

const getCharacteristicValue = (entry: HudsonCheckoutCartEntry | undefined, name: string) =>
    entry?.characteristics?.find(
        (characteristic) => characteristic.name === name && characteristic.valueType === 'string',
    )?.value as string | undefined;

const getPlanEntry = (cart: HudsonCheckoutCart) =>
    cart.entries.find((entry) => {
        const itemType = getCharacteristicValue(entry, 'ITEM_TYPE');

        return itemType === 'MobilityService' || itemType === 'FixedService';
    });

const getCommonTrackingItem = (cartEntry: HudsonCheckoutCartEntry, entryIndex: number): TrackingItem => {
    return {
        type: GTMItemType.Product,
        id: cartEntry.product.id,
        name: cartEntry.product.name,
        brand: 'Telia',
        variant: cartEntry.product.variantOption || '',
        category: '',
        listId: '',
        listName: '',
        index: entryIndex,
        originalPrice: 0,
        price: 0,
        discount: 0,
        quantity: DEFAULT_QUANTITY,
        commitmentType: '',
        previousPlan: '',
        previousOperator: '',
    };
};

const getEquipmentTrackingItem = (
    cartEntry: HudsonCheckoutCartEntry,
    entryIndex: number,
    product?: ProductDeviceInCart | ProductVariant,
    isGift?: boolean,
): TrackingItem => {
    const equipmentPrice = cartEntry.ga4Prices?.totalPrice?.value || 0;
    const equipmentDiscount = cartEntry.ga4Prices?.discount?.value || 0;
    const originalPrice = equipmentPrice + equipmentDiscount;
    const categoryCode = product?.category?.code || '';
    const categoryName = product?.category?.name || '';

    return {
        ...getCommonTrackingItem(cartEntry, entryIndex),
        type: isGift ? GTMItemType.Gift : GTMItemType.Product,
        brand: product?.brand || '',
        variant: cartEntry.product.variantOption || '',
        category: categoryName,
        listId: categoryCode,
        listName: categoryName,
        originalPrice,
        price: equipmentPrice,
        discount: 0,
    };
};

const getPlanTrackingItem = (cartEntry: HudsonCheckoutCartEntry, entryIndex: number): TrackingItem => {
    const planPrice = cartEntry.totals?.recurring?.value || 0;
    const period = getCharacteristicValue(cartEntry, 'PLAN_PERIOD');
    const minutes = getCharacteristicValue(cartEntry, 'PLAN_CALLS_ALLOWANCE');
    const sms = getCharacteristicValue(cartEntry, 'PLAN_SMS_ALLOWANCE');
    const data = getCharacteristicValue(cartEntry, 'PLAN_DATA_ALLOWANCE');
    const serviceType = getCharacteristicValue(cartEntry, 'SERVICE_TYPE') || '';

    return {
        ...getCommonTrackingItem(cartEntry, entryIndex),
        originalPrice: planPrice,
        price: planPrice,
        serviceType: GTMItemServiceType.Modification,
        commitmentType: normalizeCommitmentType(period),
        variant:
            getCharacteristicValue(cartEntry, 'ITEM_TYPE') === 'MobilityService'
                ? `${minutes} MIN, ${sms} SMS, ${data}`
                : '',
        category: serviceType,
        listName: serviceType,
    };
};

const getFixedServiceAddonTrackingItem = (cartEntry: HudsonCheckoutCartEntry, entryIndex: number): TrackingItem => {
    const price = cartEntry.totals?.recurring?.value || 0;
    const serviceType = getCharacteristicValue(cartEntry, 'SERVICE_TYPE') || '';

    return {
        ...getCommonTrackingItem(cartEntry, entryIndex),
        originalPrice: price,
        price,
        serviceType: GTMItemServiceType.Modification,
        category: serviceType,
        listName: serviceType,
    };
};

const getCheckoutItems = (
    cart: HudsonCheckoutCart,
    product?: ProductDeviceInCart | ProductVariant,
    giftProduct?: ProductDeviceInCart | ProductVariant,
): TrackingItem[] => {
    const items: TrackingItem[] = [];

    cart.entries.forEach((entry, index) => {
        const itemType = getCharacteristicValue(entry, 'ITEM_TYPE');
        const vas = getCharacteristicValue(entry, 'VAS');

        if (itemType === 'Equipment') {
            items.push(getEquipmentTrackingItem(entry, index, product));
        } else if (itemType === 'Gift') {
            items.push(getEquipmentTrackingItem(entry, index, giftProduct, true));
        } else if (itemType === 'MobilityService' || itemType === 'FixedService') {
            items.push(getPlanTrackingItem(entry, index));
        } else if (
            FIXED_SERVICE_EQUIPMENT_OR_VAS_ITEM_TYPES.includes(itemType || '') ||
            FIXED_SERVICE_VAS_CODES.includes(vas || '') ||
            Boolean(vas?.includes('GO3'))
        ) {
            items.push(getFixedServiceAddonTrackingItem(entry, index));
        }
    });

    return items;
};

export const mapHudsonCheckoutUser = ({ cart, userId }: HudsonCheckoutEventData): GTMUserData => {
    const planEntry = getPlanEntry(cart);

    return {
        user_id: userId,
        user_type: GTMUserType.Strong,
        email: cart.customer.email,
        phone: planEntry?.realizingResource?.id === 'MSISDN' ? planEntry.realizingResource.value : '',
    };
};

export const mapHudsonCheckoutToTrackingCommerce = ({
    cart,
    segment,
    product,
    giftProduct,
    ecommerce,
}: HudsonCheckoutEventData): TrackingCommerce => {
    const categoryName = product?.category?.name || '';
    const giftEntry = cart.entries.find((entry) => getCharacteristicValue(entry, 'ITEM_TYPE') === 'Gift');
    const planEntry = getPlanEntry(cart);
    const isMobOrFixService = Boolean(planEntry);
    const selectedPaymentMethod = ecommerce?.paymentMethod || cart.paymentMethod?.type;
    const paymentMethod = match(selectedPaymentMethod)
        .with(HUDSON_PAYMENT_METHOD.BankPayment, () => 'Online Banking')
        .with(HUDSON_PAYMENT_METHOD.CourierPayment, () => 'Card Payment to Courier')
        .otherwise(() => '');
    const totalValue = cart.entries.reduce((acc, entry) => {
        const totalPrice = entry.ga4Prices?.totalPrice?.value || 0;
        const authorsFee = entry.authorsFee?.value || 0;

        return acc + totalPrice + authorsFee;
    }, 0);

    const getItemListName = () => {
        if (cart.entries.length > 1) {
            return 'Mixed';
        } else if (isMobOrFixService) {
            return planEntry?.product?.name || '';
        } else {
            return categoryName || '';
        }
    };

    return {
        orderType: isMobOrFixService ? GTMOrderType.Services : GTMOrderType.Equipment,
        segment: normalizeSegment(segment),
        currency: DEFAULT_CURRENCY,
        value: isMobOrFixService ? 0 : totalValue,
        itemListName: getItemListName(),
        coupon: '',
        giftIncluded: !!giftEntry,
        paymentType:
            isMobOrFixService || (cart.totals?.recurring?.value || 0) <= 0
                ? CHECKOUT_PAYMENT_TYPE.FullPrice
                : CHECKOUT_PAYMENT_TYPE.Installment,
        paymentMethod,
        signMethod: ecommerce?.signMethod || '',
        bank: ecommerce?.bankName || cart.paymentMethod?.bank?.name || '',
        transactionId: ecommerce?.transactionId || '',
        items: getCheckoutItems(cart, product, giftProduct),
    };
};
